import { inject } from '@angular/core';
import { Router, Routes } from '@angular/router';
import { LoginComponent } from './login/login.component';
import { HomeComponent } from './home/home.component';
import { UserService } from './services/api-services/user.service';
import { SettingsComponent } from './settings/settings.component';
import { AdminAreaComponent } from './admin-area/admin-area.component';
import { CapabilityComponent } from './capability/capability.component';
import { VectorDbComponent } from './home/vector-db/vector-db.component';
import { JobPageComponent } from './capability/job-page/job-page.component';
import { ToolchainJobPageComponent } from './capability/job-page/toolchain-job-page.component';
import { CapabilityStore } from './services/capability.store';
import { ChatPageComponent } from './chat/chat-page.component';

/** Requires authentication before entering a protected page. */
const authGuard = () => {
  return inject(UserService).canActivate();
};

/** Restricts the full chat page to users with chat access. */
const chatGuard = async () => {
  const userService = inject(UserService);
  const router = inject(Router);
  if (!(await userService.canActivate())) return false;
  return userService.user()?.canAccessChat ? true : router.createUrlTree(['/dashboard']);
};

/** Waits for capability data before opening a resource page. */
const capabilitiesReadyResolver = () => {
  return inject(CapabilityStore).waitUntilCapabilitiesReady();
};

/** Maps the application pages and protected resource routes. */
export const routes: Routes = [
  { path: '', redirectTo: '/dashboard', pathMatch: 'full' },
  { path: 'login', component: LoginComponent },
  { path: 'chat', component: ChatPageComponent, canActivate: [chatGuard] },
  {
    path: 'dashboard',
    canActivate: [authGuard],
    children: [
      { path: '', component: HomeComponent },
      { path: 'vector-db', component: VectorDbComponent },
    ],
  },
  {
    path: 'admin',
    component: AdminAreaComponent,
    canActivate: [authGuard],
  },
  {
    path: 'settings',
    component: SettingsComponent,
    canActivate: [authGuard],
    runGuardsAndResolvers: 'always',
  },
  {
    path: 'tools/:toolId/results/:jobId',
    component: JobPageComponent,
    canActivate: [authGuard],
    resolve: { capabilitiesReady: capabilitiesReadyResolver },
  },
  {
    path: 'tools/:toolId/run',
    component: JobPageComponent,
    canActivate: [authGuard],
    resolve: { capabilitiesReady: capabilitiesReadyResolver },
  },
  {
    path: 'tools/:toolId',
    component: CapabilityComponent,
    canActivate: [authGuard],
    resolve: { capabilitiesReady: capabilitiesReadyResolver },
    data: { resourceType: 'tool' },
  },
  {
    path: 'toolchains/:toolchainId/run',
    component: ToolchainJobPageComponent,
    canActivate: [authGuard],
    resolve: { capabilitiesReady: capabilitiesReadyResolver },
  },
  {
    path: 'toolchains/:toolchainId',
    component: CapabilityComponent,
    canActivate: [authGuard],
    resolve: { capabilitiesReady: capabilitiesReadyResolver },
    data: { resourceType: 'toolchain' },
  },
  {
    path: 'toolchains/:toolchainId/results/:jobId',
    component: ToolchainJobPageComponent,
    canActivate: [authGuard],
    resolve: { capabilitiesReady: capabilitiesReadyResolver },
  },
];
